import { ErrorHandler, Injectable, inject } from '@angular/core';
import { GapiLoader } from 'src/app/ajs-upgraded-providers';
import { RejectOnTimeoutService } from './reject-on-timeout.service';

@Injectable({
  providedIn: 'root',
})
export class GapiLoaderService implements GapiLoader {
  private errorHandler = inject(ErrorHandler);
  private rejectOnTimeoutService = inject(RejectOnTimeoutService);
  private loadPromise?: Promise<any>;

  constructor() {
    this.ensureGlobalLoaderCallback();
  }

  load(): Promise<any> {
    const gapiWindow = window as any;

    this.ensureGlobalLoaderCallback();

    if (gapiWindow.gapiLoadingStatus === 'loaded') {
      return Promise.resolve(gapiWindow.gapi);
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    let removeGapiLoadedListener = () => {};
    const scriptLoadPromise = new Promise<any>((resolve, reject) => {
      const gapiLoaded = () => {
        resolve(gapiWindow.gapi);
      };

      removeGapiLoadedListener = () => {
        window.removeEventListener('gapi.loaded', gapiLoaded);
      };

      window.addEventListener('gapi.loaded', gapiLoaded);

      if (!gapiWindow.gapiLoadingStatus) {
        gapiWindow.gapiLoadingStatus = 'loading';
        this.injectClientScript(reject, removeGapiLoadedListener);
      }
    });

    this.loadPromise = this.rejectOnTimeoutService
      .withTimeout(scriptLoadPromise, 'gapi')
      .catch((err) => {
        this.resetLoadingState();
        this.errorHandler.handleError(err);

        throw err;
      })
      .finally(removeGapiLoadedListener);

    return this.loadPromise;
  }

  private ensureGlobalLoaderCallback() {
    const gapiWindow = window as any;

    if (gapiWindow.gapiLoadingStatus === undefined) {
      gapiWindow.gapiLoadingStatus = null;
    }

    gapiWindow.handleClientJSLoad = () => {
      gapiWindow.gapiLoadingStatus = 'loaded';

      console.debug('ClientJS is loaded.');

      window.dispatchEvent(new CustomEvent('gapi.loaded'));
    };
  }

  private injectClientScript(
    reject: (reason?: unknown) => void,
    removeGapiLoadedListener: () => void,
  ) {
    const gapiWindow = window as any;
    const src =
      gapiWindow.gapiSrc ||
      'https://apis.google.com/js/client.js?onload=handleClientJSLoad';
    const script = window.document.createElement('script');

    script.setAttribute('type', 'text/javascript');
    script.setAttribute('src', src);
    script.onerror = (error) => {
      reject(error);
      removeGapiLoadedListener();
    };

    window.document.getElementsByTagName('body')[0].appendChild(script);
  }

  private resetLoadingState() {
    const gapiWindow = window as any;

    delete gapiWindow.gapiLoadingStatus;
    this.loadPromise = undefined;
  }
}
